<script>
import useValidation from "@/use/Validation";
import useSharedData from "@/use/SharedData";
import useSharedMethod from "@/use/SharedMethod";
import useTools from "@/use/Tools";
import {computed, onMounted, ref} from "vue";
import axios from "axios";
import useSharedSelectData from "@/use/SharedSelectData";
import {useEvaluationFileIndexStore} from "@/stores/Evaluation/EvaluationFileIndex";
import {useEvaluationFileStore} from "@/stores/Evaluation/EvaluationFile";

export default {
    props: {
        addAction: Boolean,
        updateAction: Boolean,
        viewAction: Boolean,
        deleteAction: Boolean,
        type: String,
    },
    setup(props) {
        const readonly = computed(() => props.viewAction || props.deleteAction);

        const {mxRules} = useValidation();
        const {dialog, loading, overlay} = useSharedData();
        const {months, quarterlies, halfYearlies, evaluationFileScopes} = useSharedSelectData();
        const {getBack} = useSharedMethod();
        const {$refs, $route, $moment} = useTools();

        const evaluationFileIndexStore = useEvaluationFileIndexStore();
        const evaluationFileStore = useEvaluationFileStore();

        const evaluationFileIndex = ref({
            modelId: null,
            cycleYear: $moment().year()
        });

        const cycle = ref(null);

        function findById(id) {
            loading.value = true;
            axios.get(`/evaluationFileIndices/${id}`).then((response) => {
                evaluationFileIndex.value = response.data;
            }).finally(() => {
                loading.value = false;
            });
        }

        function findAllFiles() {
            loading.value = true;
            axios.get('/evaluationFiles', {
                params: {
                    type: props.type,
                    index: evaluationFileIndex.value.id,
                }
            }).then((response) => {
                evaluationFileStore.setEvaluationFiles(response.data.fileElements);
                evaluationFileStore.setEvaluationListing(response.data.listingElements);
            }).finally(() => {
                loading.value = false;
            });
        }

        function save() {
            if ($refs.form.validate()) {
                loading.value = true;
                evaluationFileIndex.value.type = props.type;
                axios.post('/evaluationFileIndices', evaluationFileIndex.value).then((response) => {
                    evaluationFileIndexStore.addEvaluationFileIndex(response.data);
                    findAllFiles();
                    getBack();
                }).finally(() => {
                    loading.value = false;
                });
            }
        }

        function update() {
            if ($refs.form.validate()) {
                loading.value = true;
                evaluationFileIndex.value.type = props.type;
                axios.put('/evaluationFileIndices', evaluationFileIndex.value).then((response) => {
                    evaluationFileIndexStore.updateEvaluationFileIndex(response.data);
                    getBack();
                }).finally(() => {
                    loading.value = false;
                });
            }
        }

        function remove() {
            if ($refs.form.validate()) {
                loading.value = true;
                axios.delete(`/evaluationFileIndices/${evaluationFileIndex.value.id}`).then(() => {
                    evaluationFileIndexStore.deleteEvaluationFileIndex(evaluationFileIndex.value);
                    getBack();
                }).finally(() => {
                    loading.value = false;
                });
            }
        }

        function findModelById() {
            if (evaluationFileIndex.value.modelId) {
                axios.get(`/evaluationFileModels/${evaluationFileIndex.value.modelId}`).then(response => {
                    cycle.value = response.data.cycle;
                });
            }
        }

        onMounted(() => {
            if (!props.addAction)
                findById($route.params.id);
        });

        return {
            evaluationFileIndexStore,
            mxRules,
            readonly,
            dialog,
            loading,
            overlay,
            evaluationFileIndex,
            cycle,
            months,
            quarterlies,
            halfYearlies,
            evaluationFileScopes,
            findModelById,
            save,
            update,
            remove,
            getBack,
        };
    }
}
</script>

<template>
    <v-dialog
        v-model="dialog"
        max-width="800"
        persistent
        scrollable
    >
        <v-card>
            <w-dialog-toolbar
                :view-action="viewAction"
                :delete-action="deleteAction"
                :add-action="addAction"
                :update-action="updateAction"
            />
            <w-overlay
                :overlay="overlay"
                :loading="loading"
                :delete-action="deleteAction"
                @remove="remove"
                @cancel="overlay = false"
            />
            <v-card-text>
                <v-container grid-list-md>
                    <v-form ref="form">
                        <w-fieldset :title="$t('common.general')">
                            <v-row dense>
                                <v-col cols="12">
                                    <w-label :label="$t('common.label')" required/>
                                    <w-text-field
                                        v-model="evaluationFileIndex.name"
                                        :rules="[mxRules.required]"
                                    />
                                </v-col>
                                <v-col cols="12" v-if="'EVALUATION_PERFORMANCE' === type">
                                    <w-label :label="$t('common.type')" required/>
                                    <w-enum-select
                                        v-model="evaluationFileIndex.scope"
                                        :items="evaluationFileScopes"
                                        not-clearable
                                        :rules="[mxRules.required]"
                                    />
                                </v-col>
                                <v-col cols="12" v-if="['EVALUATION_PERFORMANCE'].includes(type)">
                                    <w-label :label="$t('common.model')"/>
                                    <str-evaluation-file-model-autocomplete
                                        v-model="evaluationFileIndex.modelId"
                                        :rules="[mxRules.required]"
                                        @change="findModelById"
                                    />
                                </v-col>
                                <v-col cols="6" v-if="cycle === 'MONTHLY'">
                                    <w-label :label="$t('common.month')" required/>
                                    <w-enum-select
                                        v-model="evaluationFileIndex.cycleMonth"
                                        :items="months"
                                        :rules="[mxRules.required]"
                                    />
                                </v-col>
                                <v-col cols="6" v-if="cycle === 'QUARTERLY'">
                                    <w-label :label="$t('common.QUARTERLY')" required/>
                                    <w-enum-select
                                        v-model="evaluationFileIndex.cycleQuarterlyOrHalfYearly"
                                        :items="quarterlies"
                                        :rules="[mxRules.required]"
                                    />
                                </v-col>
                                <v-col cols="6" v-if="cycle === 'HALF_YEARLY'">
                                    <w-label :label="$t('common.HALF_YEARLY')" required/>
                                    <w-enum-select
                                        v-model="evaluationFileIndex.cycleQuarterlyOrHalfYearly"
                                        :items="halfYearlies"
                                        :rules="[mxRules.required]"
                                    />
                                </v-col>
                                <v-col cols="12" v-if="evaluationFileIndex.modelId">
                                    <w-label :label="$t('common.year')" required/>
                                    <w-text-field
                                        v-model="evaluationFileIndex.cycleYear"
                                        type="number"
                                        :rules="[mxRules.required]"
                                    />
                                </v-col>

                            </v-row>
                        </w-fieldset>
                        <br/>
                        <w-fieldset :title="$t('common.concerned')">
                            <v-row dense>
                                <v-col cols="12" v-if="evaluationFileIndex.scope === 'OCCUPATION'">
                                    <w-label :label="$t('module.occupations')" required/>
                                    <str-occupation-autocomplete
                                        v-model="evaluationFileIndex.occupationIds"
                                        multiple
                                        :readonly="readonly"
                                        :rules="[mxRules.listRequired]"
                                    />
                                </v-col>
                                <v-col cols="12" v-if="evaluationFileIndex.scope === 'WORKER'">
                                    <w-label :label="$t('module.workers')" required/>
                                    <str-worker-autocomplete
                                        v-model="evaluationFileIndex.workerIds"
                                        multiple
                                        :readonly="readonly"
                                        :rules="[mxRules.listRequired]"
                                    />
                                </v-col>
                            </v-row>
                        </w-fieldset>
                    </v-form>
                </v-container>
            </v-card-text>
            <v-divider/>
            <v-card-actions>
                <v-spacer/>
                <w-btn-close @press="getBack"/>
                <w-btn-add
                    :show="addAction"
                    :loading="loading"
                    @press="save"
                />
                <w-btn-update
                    :show="updateAction"
                    :loading="loading"
                    @press="update"
                />
                <w-btn-delete
                    :show="deleteAction"
                    :loading="loading"
                    @press="overlay = true"
                />
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>